import React from 'react';
import { Card } from 'primereact/card';
import { Tag } from 'primereact/tag';
import { Message } from 'primereact/message';
import { useLanguage } from '../../../../../contexts/LanguageContext';
import { resolveTargetTypeText, resolveOperationText, operationSeverity, resolveFieldLabel, resolveUnifiedSection, UnifiedFieldSection, camelizeKeys, ChangeOperation, ChangeTargetType } from '../../../../../utils/dealerApprovalEnums';
// Yanıp sönme animasyonu + değişiklik kalemi stilleri modüler SCSS'ten gelir.
import '../../dealerApproval.scss';

const safeParse = (json) => {
    if (!json) return null;
    if (typeof json === 'object') return json;
    try {
        return JSON.parse(json);
    } catch {
        return null;
    }
};

// Değer -> okunaklı metin. Boş değerler tire ile gösterilir; nesne/dizi gizlenir.
// Boolean alanlar dile göre Evet / Hayır gösterilir.
const formatValue = (v, th) => {
    if (v === null || v === undefined || v === '') return '—';
    if (typeof v === 'boolean') return v ? th('common.yes') : th('common.no');
    return String(v);
};

// payloadJson'dan gösterilebilir (skaler) alan anahtarları. Teknik/kimlik alanları gizlenir.
const HIDDEN_FIELDS = new Set(['id', 'dealerAddressBrandId', 'siteUseId', 'requestId', 'approvalId', 'languageId', 'dealerId', 'brandId', 'sapAddrId']);
const payloadFieldKeys = (payload) => Object.keys(payload || {}).filter((k) => !HIDDEN_FIELDS.has(k) && payload[k] !== null && payload[k] !== undefined && typeof payload[k] !== 'object');

const SECTION_ORDER = [UnifiedFieldSection.DEALER, UnifiedFieldSection.ADDRESS, UnifiedFieldSection.BRAND, UnifiedFieldSection.OTHER];
const SECTION_TITLE = {
    [UnifiedFieldSection.DEALER]: 'dealerRegistration.form.dealerInfo',
    [UnifiedFieldSection.ADDRESS]: 'dealerRegistration.form.addressInfo',
    [UnifiedFieldSection.BRAND]: 'dealerRegistration.form.brandInfo',
    [UnifiedFieldSection.OTHER]: 'dealerApproval.otherSection'
};

// Güncelleme (UPDATE) alanı: etiket üstte, altında Eski -> Yeni (sarmalı).
const UpdateFieldRow = ({ th, fieldKey, change }) => (
    <div className="da-change-field">
        <div className="da-change-field__head">
            <i className="pi pi-exclamation-circle text-orange-500 dealer-approval-blink" />
            <span className="da-change-field__label">{resolveFieldLabel(th, fieldKey)}</span>
            <Tag value={th('dealerApproval.updatedBadge')} severity="warning" />
        </div>
        <div className="da-change-field__values">
            <span className="da-change-old">{formatValue(change?.old ?? change?.Old, th)}</span>
            <i className="pi pi-arrow-right da-change-arrow" />
            <span className="da-change-new">{formatValue(change?.new ?? change?.New, th)}</span>
        </div>
    </div>
);

// Yeni/Ekle veya Sil alanı: etiket + tek değer (yeni ise yeşil + "yeni" rozeti).
const ValueFieldRow = ({ th, fieldKey, value, isNew }) => (
    <div className={`da-change-field ${isNew ? 'da-change-field--new' : ''}`}>
        <div className="da-change-field__head">
            <span className="da-change-field__label">{resolveFieldLabel(th, fieldKey)}</span>
            {isNew && <Tag value={th('dealerApproval.newBadge')} severity="success" />}
        </div>
        <div className="da-change-field__values">
            <span className={isNew ? 'da-change-new' : ''}>{formatValue(value, th)}</span>
        </div>
    </div>
);

// Bir değişiklik kaleminin gövdesi: UNIFIED ise Bayi/Adres/Marka bölümlerine
// gruplanır; sekme (10-16) kalemleri düz liste olarak gösterilir.
const ChangeBody = ({ c, forceNew }) => {
    const { th } = useLanguage();
    const payload = camelizeKeys(safeParse(c.payloadJson)) || {};
    const changedFields = camelizeKeys(safeParse(c.changedFieldsJson));
    const isDelete = c.operation === ChangeOperation.DELETE;
    const isUpdate = !forceNew && c.operation === ChangeOperation.UPDATE && changedFields && Object.keys(changedFields).length > 0;

    let keys;
    let renderRow;
    if (isUpdate) {
        keys = Object.keys(changedFields);
        renderRow = (k) => <UpdateFieldRow key={k} th={th} fieldKey={k} change={changedFields[k]} />;
    } else {
        // Insert / yeni kayıt -> "yeni"; Silme -> salt değer (rozetsiz).
        keys = payloadFieldKeys(payload);
        renderRow = (k) => <ValueFieldRow key={k} th={th} fieldKey={k} value={payload[k]} isNew={!isDelete} />;
    }

    if (keys.length === 0) return null;

    if (c.targetType === ChangeTargetType.UNIFIED) {
        const sections = keys.reduce((acc, k) => {
            const sec = resolveUnifiedSection(k);
            (acc[sec] = acc[sec] || []).push(k);
            return acc;
        }, {});
        return (
            <div className="da-change-sections">
                {SECTION_ORDER.filter((sec) => sections[sec]?.length).map((sec) => (
                    <div key={sec} className="da-change-section">
                        <div className="da-change-section__title">{th(SECTION_TITLE[sec])}</div>
                        <div className="da-change-section__body">{sections[sec].map(renderRow)}</div>
                    </div>
                ))}
            </div>
        );
    }

    return <div className="da-change-list">{keys.map(renderRow)}</div>;
};

const ApprovalChanges = ({ changes = [], isNewRegistration = false }) => {
    const { th } = useLanguage();

    // Yeni kayıt: prop öncelikli. Backend prop göndermezse, tüm kalemler INSERT
    // veya changedFieldsJson'suz ve en az bir UNIFIED varsa yeni kayıt kabul edilir.
    const detectedNew = isNewRegistration || (changes.length > 0 && changes.every((c) => c.operation === ChangeOperation.INSERT || !c.changedFieldsJson) && changes.some((c) => c.targetType === ChangeTargetType.UNIFIED));

    return (
        <div>
            <h5>{th('dealerApproval.changesTitle')}</h5>
            {detectedNew && changes.length > 0 && <Message severity="success" text={th('dealerApproval.newRegistrationInfo')} className="mb-3 w-full" />}
            {changes.length === 0 && <div className="text-500">{th('dealerApproval.noChanges')}</div>}
            {changes.map((c) => {
                const isDelete = c.operation === ChangeOperation.DELETE;
                return (
                    <Card key={c.id} className={`da-change-card mb-2 ${isDelete ? 'da-change-card--delete' : ''}`}>
                        <div className="da-change-card__head">
                            <span className="da-change-card__title">{resolveTargetTypeText(th, c.targetType)}</span>
                            {detectedNew ? <Tag value={th('dealerApproval.newBadge')} severity="success" /> : <Tag value={resolveOperationText(th, c.operation)} severity={operationSeverity(c.operation)} />}
                            {!detectedNew && isDelete && <Tag value={th('dealerApproval.deletedBadge')} severity="danger" />}
                        </div>
                        {(c.addressName || c.brandName) && <div className="text-sm text-600 mb-2">{[c.addressName, c.brandName].filter(Boolean).join(' — ')}</div>}
                        <ChangeBody c={c} forceNew={detectedNew} />
                    </Card>
                );
            })}
        </div>
    );
};

export default ApprovalChanges;
